import { getAccessToken } from './supabase.js';

const FN_GENERATE = import.meta.env.VITE_FN_GENERATE;

/**
 * Calls the Supabase Edge Function `generate`, which:
 * - Verifies the user JWT
 * - Enforces plan limits server-side
 * - Logs the generation
 * - Streams Anthropic's SSE response back to us
 *
 * Returns once the stream completes, after invoking onChunk for each text delta.
 * Throws on rate-limit / quota / auth / network errors.
 */
export async function streamGeneration({ prompt, type, mode, maxTokens = 4096 }, onChunk, signal) {
  if (!FN_GENERATE) throw new Error('VITE_FN_GENERATE not configured');
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(FN_GENERATE, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ prompt, type, mode, maxTokens }),
    signal
  });

  if (!res.ok) {
    let msg = `Generation failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (!data || data === '[DONE]') continue;
      try {
        const evt = JSON.parse(data);
        if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta') {
          onChunk(evt.delta.text || '');
        }
      } catch {}
    }
  }
}
